import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import axios from 'axios';
import { trackSearch } from './services/analytics';
import SEO from './components/SEO';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import SearchBar from './components/SearchBar';
import DocumentGrid from './components/DocumentGrid';
import Basket from './components/Basket';
import BottomBar from './components/BottomBar';

const CRAWLER_API = process.env.REACT_APP_CRAWLER_URL || 'http://localhost:4000';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#009688',
      light: '#4db6ac',
      dark: '#00796b',
    },
    secondary: {
      main: '#26a69a',
    },
    background: {
      default: '#0a0a0a',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b2dfdb',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 150, 136, 0.2)',
        },
      },
    },
  },
});

function App() {
  const [documents, setDocuments] = useState([]);
  const [basket, setBasket] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crawlStats, setCrawlStats] = useState(null);
  const [basketOpen, setBasketOpen] = useState(false);
  const [showLanding, setShowLanding] = useState(true);

  const handleSearch = async (url, keywords, school, year) => {
    setLoading(true);
    setCrawlStats({ documentsFound: 0, pagesCrawled: 0 });
    
    try {
      const response = await axios.post(`${CRAWLER_API}/crawl`, {
        url,
        keywords,
        school,
        year
      });

      if (response.data.success) {
        setDocuments(response.data.documents);
        setCrawlStats({
          documentsFound: response.data.count,
          pagesCrawled: response.data.pagesCrawled || 0
        });
        
        // Track search analytics (non-blocking)
        trackSearch(keywords, school, year, response.data.count).catch(err => 
          console.error('Analytics error:', err)
        );
      } else {
        console.error('Crawl failed:', response.data.error);
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error calling crawler API:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const addToBasket = (doc) => {
    if (!basket.find(item => item.id === doc.id)) {
      setBasket([...basket, doc]);
    }
  };

  const removeFromBasket = (docId) => {
    setBasket(basket.filter(item => item.id !== docId));
  };

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SEO />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header 
          basketCount={basket.length} 
          onBasketClick={() => setBasketOpen(true)}
        />
        
        {showLanding ? (
          <LandingPage onGetStarted={() => setShowLanding(false)} />
        ) : (
          <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, mb: (loading || crawlStats) ? 8 : 0 }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
              <SearchBar onSearch={handleSearch} loading={loading} />
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mt: 3 }}>
                <Box>
                  <DocumentGrid 
                    documents={documents} 
                    onAddToBasket={addToBasket}
                    loading={loading}
                  />
                </Box>
                
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                  <Basket 
                    items={basket} 
                    onRemove={removeFromBasket}
                    mobileOpen={basketOpen}
                    onMobileClose={() => setBasketOpen(false)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {(loading || crawlStats) && <BottomBar crawling={loading} stats={crawlStats} />}
      </Box>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
